import { AxiosError } from 'axios'
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  CandidateInterviewListSliceState,
  GetInterviewCandidateListType,
  InterviewCandidateListConfigType,
  InterviewCandidateListType,
} from '../../../types/Recruitment/CandidateList/CandidateInterviewListTypes'
import candidateListApi from '../../../middleware/api/Recruitment/CandidateList/CandidateListApi'
import { ValidationError } from '../../../types/commonTypes'
import { RootState } from '../../../stateStore'
import { initialCandidateInterviewFilterValues } from '../../../pages/Recruitment/CandidateList/CandidateListHelpers'

const getInterviewCandidatesList = createAsyncThunk(
  'candidateInterviewList/getInterviewCandidatesList',
  async (props: GetInterviewCandidateListType, thunkApi) => {
    try {
      return await candidateListApi.getInterviewCandidatesList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const exportInterviewCandidatesList = createAsyncThunk(
  'candidateList/exportInterviewCandidatesList',
  async (props: InterviewCandidateListConfigType, thunkApi) => {
    try {
      return await candidateListApi.exportInterviewCandidatesList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

export const initialCandidateInterviewListState: CandidateInterviewListSliceState =
  {
    isLoading: ApiLoadingState.idle,
    listSize: 0,
    InterviewCandidateList: [],
    InterviewCandidateListBody: initialCandidateInterviewFilterValues, //* always set the filter values to initial values
  }

const candidateInterviewListSlice = createSlice({
  name: 'candidateInterviewList',
  initialState: initialCandidateInterviewListState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setInterviewCandidateListBody: (state, action) => {
      state.InterviewCandidateListBody = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInterviewCandidatesList.fulfilled, (state, action) => {
        state.InterviewCandidateList = action.payload.list
        state.listSize = action.payload.size
      })
      .addMatcher(isAnyOf(getInterviewCandidatesList.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addMatcher(isAnyOf(getInterviewCandidatesList.fulfilled), (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addMatcher(isAnyOf(getInterviewCandidatesList.rejected), (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

const getLoading = (state: RootState): string =>
  state.candidateInterviewList.isLoading
const getInterviewCandidateList = (
  state: RootState,
): InterviewCandidateListType[] =>
  state.candidateInterviewList.InterviewCandidateList
const getInterviewCandidateListSize = (state: RootState): number =>
  state.candidateInterviewList.listSize
const getInterviewCandidateListBody = (
  state: RootState,
): GetInterviewCandidateListType =>
  state.candidateInterviewList.InterviewCandidateListBody

export const candidateInterviewListThunk = {
  getInterviewCandidatesList,
  exportInterviewCandidatesList,
}

export const candidateInterviewListSelectors = {
  getLoading,
  getInterviewCandidateList,
  getInterviewCandidateListSize,
  getInterviewCandidateListBody,
}

export const candidateInterviewListService = {
  ...candidateInterviewListThunk,
  actions: candidateInterviewListSlice.actions,
  selectors: candidateInterviewListSelectors,
}

export default candidateInterviewListSlice.reducer
